import React, { useEffect, useState, useRef } from "react";
import { Star, Quotes } from "@phosphor-icons/react";

/**
 * TestimonialsRail — auto-rotating customer success-stories carousel.
 * Desktop: 3 cards visible at once, fade-rotates every 5s.
 * Mobile: single card, slide-rotates every 5s with dot indicators + swipe-friendly.
 */
const TESTIMONIALS = [
  {
    name: "Priya S.",
    city: "Bengaluru",
    rating: 5,
    quote:
      "I came to Saraswati ji broken after my engagement was called off. In one session she pulled the Star card and said the right person would come in 7 months — and he did. I'm getting married this December!",
    astrologer: "Saraswati Devi · Tarot",
    initials: "PS",
    tint: "from-amber-100 to-rose-50",
  },
  {
    name: "Arjun M.",
    city: "Mumbai",
    rating: 5,
    quote:
      "Anushri ji's Lal Kitab remedy for my Saturn placement was so simple — feeding crows for 43 days. I laughed at first. Three months later I cracked my promotion. Still doing the remedy.",
    astrologer: "Anushri Karandikar · Lal Kitab",
    initials: "AM",
    tint: "from-orange-100 to-amber-50",
  },
  {
    name: "Divya R.",
    city: "Hyderabad",
    rating: 5,
    quote:
      "Kanaka ma'am ran my Lo Shu grid and told me my business name had a wrong vibration. Just one letter change in the logo. Revenue is 2.4x in 5 months. I owe her a temple visit.",
    astrologer: "Kanaka Subramanian · Numerology",
    initials: "DR",
    tint: "from-emerald-100 to-amber-50",
  },
  {
    name: "Rohit & Tanvi",
    city: "Pune",
    rating: 5,
    quote:
      "We got our Ashta-koota matching done before saying yes to the alliance. 28/36 with a clear yes on Bhakoot. We're engaged now. Saraswati ji's reading sealed the deal for both families.",
    astrologer: "Saraswati Devi · Compatibility",
    initials: "RT",
    tint: "from-rose-100 to-amber-50",
  },
  {
    name: "Meera J.",
    city: "Delhi",
    rating: 5,
    quote:
      "I was anxious about my mother's surgery. The astrologer didn't sugar-coat it — but said the worst phase was already past peak. Surgery went well. The honest, non-doom tone is what I'll remember.",
    astrologer: "Acharya Surekha Joshi · Vedic D1–D10",
    initials: "MJ",
    tint: "from-sky-100 to-amber-50",
  },
  {
    name: "Karan B.",
    city: "Chandigarh",
    rating: 5,
    quote:
      "Sunita ji read my palm over chat and pinpointed my career change to age 28 — it happened within 4 months. The Fate Line break was on the exact spot she described.",
    astrologer: "Sunita Rao · Palmistry",
    initials: "KB",
    tint: "from-amber-100 to-orange-50",
  },
  {
    name: "Aanya P.",
    city: "Kolkata",
    rating: 5,
    quote:
      "I came in low after losing my job. Anjali ji's empathy floored me — she heard me first, then numbers second. Got a new offer in 6 weeks. Royal seekers programme is worth every rupee.",
    astrologer: "Anjali Kapoor · Numerology",
    initials: "AP",
    tint: "from-amber-100 to-rose-50",
  },
  {
    name: "Vikram T.",
    city: "Ahmedabad",
    rating: 5,
    quote:
      "Rajat ji's D9 navamsa reading for marriage timing was spot-on. He flagged Rahu in 7th and gave a Mercury remedy. Met my wife exactly in the window he predicted.",
    astrologer: "Rajat Chopra · Navamsa D9",
    initials: "VT",
    tint: "from-orange-100 to-amber-50",
  },
];

const ROTATION_MS = 5500;

export default function TestimonialsRail() {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const total = TESTIMONIALS.length;
  const railRef = useRef(null);

  useEffect(() => {
    if (paused) return;
    const id = setInterval(() => setIndex((i) => (i + 1) % total), ROTATION_MS);
    return () => clearInterval(id);
  }, [paused, total]);

  // Show 3 cards starting at `index` (wraps around).
  const visible = [0, 1, 2].map((offset) => TESTIMONIALS[(index + offset) % total]);

  return (
    <section
      className="max-w-7xl mx-auto px-6 lg:px-10 py-16 sm:py-24 border-t border-soft"
      data-testid="testimonials-section"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-10 sm:mb-12">
        <div>
          <p className="label-eyebrow">Stories from our seekers</p>
          <h2 className="font-serif-display text-4xl sm:text-5xl mt-4 font-light max-w-2xl">
            Real readings, <span className="text-gold italic">real outcomes.</span>
          </h2>
        </div>
        <div className="flex items-center gap-2 text-sm text-muted" data-testid="testimonials-rating">
          <Star size={16} weight="fill" className="text-gold" />
          <span className="font-semibold text-[#3F2D1B]">4.83</span>
          <span>· 12,400+ readings rated</span>
        </div>
      </div>

      {/* Desktop: 3 cards rotation */}
      <div
        className="hidden md:grid md:grid-cols-3 gap-5"
        ref={railRef}
        data-testid="testimonials-grid"
      >
        {visible.map((t, slot) => (
          <TestimonialCard key={`${t.name}-${index}-${slot}`} t={t} />
        ))}
      </div>

      {/* Mobile: single-card slider with dot indicators */}
      <div className="md:hidden" data-testid="testimonials-mobile">
        <TestimonialCard t={TESTIMONIALS[index]} key={`m-${index}`} />
        <div className="flex items-center justify-center gap-1.5 mt-5">
          {TESTIMONIALS.map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => setIndex(i)}
              className={`h-1.5 rounded-full transition-all ${
                i === index ? "w-6 bg-[#D4AF37]" : "w-1.5 bg-[#3F2D1B]/20"
              }`}
              aria-label={`Go to testimonial ${i + 1}`}
              data-testid={`testimonial-dot-${i}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

function TestimonialCard({ t }) {
  return (
    <article
      className={`relative card-surface rounded-2xl p-6 sm:p-7 bg-gradient-to-br ${t.tint} border border-[#3F2D1B]/10 overflow-hidden animate-[fadeUp_500ms_ease-out]`}
      data-testid="testimonial-card"
    >
      <Quotes
        size={64}
        weight="fill"
        className="absolute -top-2 -right-2 text-[#D4AF37]/15"
      />
      <div className="flex items-center gap-3 mb-4">
        <div className="w-11 h-11 rounded-full bg-gradient-to-br from-[#3F2D1B] to-[#7c4f2a] text-amber-200 flex items-center justify-center font-serif-display text-base shadow-sm">
          {t.initials}
        </div>
        <div className="min-w-0">
          <div className="font-serif-display text-lg text-[#3F2D1B] truncate">{t.name}</div>
          <div className="text-xs text-[#3F2D1B]/60">{t.city}</div>
        </div>
        <div className="ml-auto flex items-center gap-0.5">
          {Array.from({ length: t.rating }).map((_, i) => (
            <Star key={i} size={13} weight="fill" className="text-gold" />
          ))}
        </div>
      </div>
      <p className="text-[#3F2D1B] text-sm sm:text-[15px] leading-relaxed">"{t.quote}"</p>
      <div className="mt-5 pt-4 border-t border-[#3F2D1B]/10 label-eyebrow !text-[10px] text-[#3F2D1B]/70">
        Read by · {t.astrologer}
      </div>
      <style>{`
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </article>
  );
}
